import type { CareSeekerShallowLeadFormData } from "~care/types/forms/care-seeker-shallow-lead-form";

import { useNearbyLocations } from "@homeinstead/geolocation/hooks";
import { cn } from "@homeinstead/ui";
import { AnimatePresence, motion } from "motion/react";
import React, { Suspense } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { Input } from "~care/components/ui/Input/Input";
import { LocationSelect } from "~care/components/ui/LocationSelect/LocationSelect";
import { ServiceLocationTrackedClickEvent } from "~care/types/events";
import { FormError } from "../../FormError/FormError";
import ContinueButton from "./ContinueButton";
import { Testimony } from "./Testimony";
import { testimonies } from "./testimonies";

const LocationStep = ({
  nextStep,
  className,
}: {
  className?: string;
  nextStep: () => void;
}) => {
  const { register, trigger, watch, control, getFieldState } =
    useFormContext<CareSeekerShallowLeadFormData>();

  // This is the raw user input (postal code typed by user)
  const location = watch("step4.location", "");

  // This is the final derived postal code from the selected organization
  const finalPostalCode = watch("step4.postalCode", "");

  // The selected office number
  const franchiseNumber = watch("step4.organizationNumber", 0);

  const { invalid: invalidInput } = getFieldState("step4.location");

  const {
    data: nearbyLocations,
    isFetching: isFetchingNearbyLocations,
    error: errorOnNearbyLocations,
  } = useNearbyLocations({
    config: { apiEndpoint: "/api/geo-search" },
    location: location, // pass in the user typed code
  });

  const franchiseOptions = nearbyLocations?.organizations ?? [];

  const trackingEvent = {
    eventName: "tracked_click",
    eventPayloadGenerator: (path: string, url: string) => {
      return ServiceLocationTrackedClickEvent(
        Number(finalPostalCode),
        Number(franchiseNumber),
        path,
        url,
      );
    },
  };

  // For the continue button
  const userPostalCodeValid = !!location.trim() && !invalidInput;
  const franchiseNumberValid = franchiseNumber !== 0;
  const locationsAvailable = franchiseOptions.length > 0;
  const formReady =
    userPostalCodeValid && franchiseNumberValid && locationsAvailable;

  const shouldShowLocationSelect =
    !!location && // user typed something
    !invalidInput && // user’s input is not invalid
    !errorOnNearbyLocations;

  return (
    <div
      className={cn(
        "flex h-full flex-col gap-4 gap-y-24 xl:flex-row xl:gap-y-4",
        className,
      )}
    >
      <div className="flex w-full flex-col gap-4 xl:max-w-[440px]">
        <h1 className="text-left font-editorial-new font-light text-1xl">
          Where is care needed?
        </h1>
        <p className="mb-4 text-base text-new-beginnings-base">
          Please enter the location where in-home care is needed.
        </p>

        <div className="max-w-[440px]">
          <Controller
            control={control}
            name="step4.location"
            render={({ field, fieldState }) => {
              return (
                <div className="max-w-[440px]">
                  <input
                    type="hidden"
                    value={finalPostalCode}
                    {...register("step4.postalCode")}
                  />
                  <Input
                    className="peer bg-white text-base text-new-beginnings-base"
                    placeholder="Postal Code"
                    type="text"
                    {...field}
                    // Override onChange to trigger validation on each keystroke
                    onChange={(e) => {
                      field.onChange(e);
                      trigger("step4.location");
                    }}
                  />
                  <input
                    type="hidden"
                    value={franchiseNumber}
                    {...register("step4.organizationNumber")}
                  />
                  {fieldState.error && (
                    <FormError.span className="mt-2 max-w-[440px]">
                      {fieldState.error.message}
                    </FormError.span>
                  )}
                </div>
              );
            }}
          />
        </div>

        {/* Show the select block only if user’s input is valid & we have data */}

        <Suspense fallback={<p className="w-full">Loading locations...</p>}>
          {isFetchingNearbyLocations && shouldShowLocationSelect ? (
            <p>Loading locations...</p>
          ) : errorOnNearbyLocations ? (
            <FormError.p>Failed to load locations.</FormError.p>
          ) : (
            shouldShowLocationSelect && (
              <AnimatePresence mode="wait">
                {franchiseOptions.length > 0 ? (
                  <motion.div
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -5 }}
                    initial={{ opacity: 0, y: -5 }}
                    key="location-select"
                    transition={{ duration: 0.2, ease: "easeInOut" }}
                  >
                    <LocationSelect
                      franchiseOptions={franchiseOptions}
                      organizationNumberFieldName={"step4.organizationNumber"}
                      postalCodeFieldName={"step4.postalCode"}
                    />
                  </motion.div>
                ) : (
                  location && (
                    <motion.p
                      animate={{ opacity: 1, y: 0 }}
                      className="text-error text-sm"
                      exit={{ opacity: 0, y: -5 }}
                      initial={{ opacity: 0, y: -5 }}
                      key="no-locations"
                      transition={{ duration: 0.2, ease: "easeInOut" }}
                    >
                      No locations found.
                    </motion.p>
                  )
                )}
              </AnimatePresence>
            )
          )}
        </Suspense>

        {formReady && (
          <ContinueButton
            isEnabled
            nextStep={nextStep}
            trackingEvent={trackingEvent}
          />
        )}
      </div>

      <Testimony className="mb-auto ml-auto flex max-w-md flex-col gap-4">
        {testimonies.step4}
      </Testimony>
    </div>
  );
};

export default LocationStep;
